<script setup>
import { computed } from 'vue'
import { useDisplay } from 'vuetify'
import { formatNumber } from '@/utils/strings'
import { useI18n } from 'vue-i18n'
import { extractDate, extractTime, formatRelativeDay } from '@/utils/datetime'
import { useCalendarStore } from '@/views/apps/calendar/useCalendarStore'
import EmployeeAvatar from '@/components/EmployeeAvatar.vue'
import StatusChip, { getStatusStyle } from '@/components/StatusChip.vue'
import EmployeeAvatarBlock from "@/components/EmployeeAvatarBlock.vue"
/**
 * Single visit presented as a card (3 zones: when · what · money).
 *
 * Data-source agnostic — always feed it the normalized shape below.
 * For /statistics/visits/index use mapStatsVisitToCard() from ./mapStatsVisit.js
 *
 * @typedef {Object} VisitCardData
 * @property {number|string} id
 * @property {string|Date} date                              date + time of the visit
 * @property {{ name: string, color: string }} [status]      color = Vuetify theme token
 * @property {string} [source]                               where the visit came from (backend label)
 * @property {string} [sourceIcon]                            icon for that source, resolved by the mapper
 * @property {{ id: *, name: string }} [employee]
 * @property {{ id: *, name: string, phone: string }} [client]
 * @property {Array<{ title: string, price: number, priceAfterDiscount: number, employee: string }>} [services]
 * @property {Array<{ name: string, quantity: number, value: number, employee: string }>} [products]  sold products
 * @property {Array<{ name: string, quantity: number, value: number, employee: string }>} [usedProducts]  cosmetics used up
 * @property {Array<{ name: string, value: number, isPrepayment: boolean }>} [payments]
 * @property {Array<{ label: string, value: number }>} [discounts]
 * @property {number|null} [paid]
 * @property {number} [tip]
 * @property {{ gained: number, spent: number }} [loyaltyPoints]
 * @property {string} [cancelReason]                         translated cancellation reason
 * @property {string} [cancelComment]
 * @property {'visit'|'sale'} [kind]                         'sale' = standalone product sale
 * @property {boolean} [muted]                               dim the card (cancelled / deleted)
 */
const props = defineProps({
  visit: {
    type: Object,
    required: true,
  },
  clickableEmployee: {
    type: Boolean,
    default: false,
  },
  clickableCustomer: {
    type: Boolean,
    default: true,
  },
  showRealVisitDuration: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['employeeClick', 'customerClick'])

const { mobile, sm, md, xl, mdAndDown, lgAndDown, lgAndUp, xlAndUp, mdAndUp, width } = useDisplay()
const calendarStore = useCalendarStore()
const { locale } = useI18n()

const money = value => formatNumber(value, true, calendarStore.currency)
const showLoyaltyPoints = calendarStore.loyaltyProgramSettings.enabled

const visitDate = computed(() => new Date(props.visit.date))
const visitDateEnd = computed(() => new Date(props.visit.dateEnd))

// "pojutrze" / "za 3 dni" / "wczoraj" / "3 tygodnie temu" — works both ways round
const relativeDay = computed(() => formatRelativeDay(visitDate.value, locale.value))

// A standalone product sale has no end time and no duration
const hasEndTime = computed(() => Boolean(props.visit.dateEnd))

const loyaltyPoints = computed(() => ({
  gained: props.visit.loyaltyPoints?.gained ?? 0,
  spent: props.visit.loyaltyPoints?.spent ?? 0,
}))

// Package size of the product itself, e.g. 100 + 'ml' -> '100 ml'. Number() drops trailing zeros.
const packageLabel = product => [Number(product.package), product.unit].filter(Boolean).join(' ')

// Item-level employee is only worth showing when services/products were handled by different people
const showItemEmployee = computed(() => new Set([
  ...(props.visit.services || []),
  ...(props.visit.products || []),
  ...(props.visit.usedProducts || []),
].map(item => (item.employee ?? '').trim()).filter(Boolean)).size > 1)

// Combined visit = its services were performed by more than one employee
const isCombined = computed(() => new Set(
  (props.visit.services || []).map(service => (service.employee ?? '').trim()).filter(Boolean),
).size > 1)

// visit.employee carries only { id, name } — avatar color/image live on the calendar employee
const employeeAvatar = computed(() => {
  const employee = props.visit.employee
  if (!employee?.name && !employee?.shortName) return null

  const calendarEmployee = calendarStore.employeesCalendars
    .find(item => String(item.employeeId) === String(employee.id))

  return {
    ...calendarEmployee,
    ...employee,
    id: calendarEmployee?.id || employee.id,
    shortName: employee.shortName,
    title: employee.shortName || employee.name,
  }
})

const isDiscounted = service =>
  service.priceAfterDiscount != null && service.priceAfterDiscount !== service.price

const statusBorderColor = computed(() =>
  getStatusStyle(
    props.visit.status?.id,
    calendarStore.viewSettings.clientHasToConfirm,
  ).borderColor,
)
</script>

<template>
  <VCard
    variant="outlined"
    class="d-flex flex-column flex-md-row border-s-lg"
    :style="`border-inline-start-color: ${statusBorderColor} !important;`"
    elevation="0"
  >
    <!-- ======== WHEN ======== -->
    <div
      class="pa-3 d-flex ga-4 flex-column justify-space-between"
      :style="[
        { minInlineSize: lgAndUp ? '300px' : mdAndDown && !mobile ? '275px' : undefined },
        { maxInlineSize: lgAndUp ? '300px' : mdAndDown && !mobile ? '275px' : undefined },
      ]"
    >
      <div class="flex-shrink-0 d-flex flex-row ga-4 justify-space-between">
        <div
          class=""
          style="margin-top: 2px;"
        >
          <div class="text-h6 font-weight-bold d-flex flex-column ga-0">
            <span>{{ extractDate(visitDate) }}</span>
            <span
              v-if="relativeDay"
              class="text-caption text-disabled"
              style="margin-top: -1px"
            >
              {{ relativeDay }}
            </span>
          </div>

          <div class="text-caption text-medium-emphasis d-flex ga-1 align-center">
            <span>{{ extractTime(visitDate) }}</span>
            <template v-if="hasEndTime">
              <VIcon
                icon="ri-arrow-right-line"
                size="16"
                class="text-disabled"
              />
              <span>{{ extractTime(visitDateEnd) }}</span>
            </template>
          </div>
          <div class="d-flex align-center ga-2">
            <div v-if="visit?.duration">
              <VChip
                size="x-small"
                class=""
                label
              >
                <VIcon
                  size="small"
                  class="mr-1"
                  icon="ri-hourglass-line"
                />
                {{ visit.duration }} min
                <!--              <VTooltip activator="parent">-->
                <!--                {{ $t('duration') }}-->
                <!--              </VTooltip>-->
              </VChip>
            </div>

            <div v-if="visit?.realDuration && props.showRealVisitDuration">
              <VChip
                size="x-small"
                :color="visit?.realDuration === visit?.duration ? '' : visit?.realDuration > visit?.duration ? 'warning' : 'green'"
                class=""
                label
              >
                <VIcon
                  size="small"
                  class="mr-1"
                  icon="ri-timer-line"
                />
                {{ visit.realDuration }} min
                <VTooltip activator="parent">
                  {{ $t('real_duration') }}
                </VTooltip>
              </VChip>
            </div>
          </div>

        </div>
        <div class="d-flex flex-column align-end">
          <template v-if="visit.status">
            <StatusChip :status="visit.status" />
          </template>
          <div
            v-if="visit.source"
            class="d-flex align-center ga-1 text-caption text-disabled lh-1 text-right"
          >
            {{ visit.source }}
            <VIcon
              :icon="visit.sourceIcon || 'ri-global-line'"
              size="14"
            />
          </div>
          <!--          <small-->
          <!--            v-if="isCombined"-->
          <!--            class="custom-chip chip-info mt-1 px-2"-->
          <!--          >{{ $t('fc.combined_visit') }}</small>-->
          <VChip
            v-if="isCombined"
            size="x-small"
            variant="flat"
            class="mt-1"
            color="combined"
            label
          >
            {{ $t('fc.combined_visit') }}
          </VChip>
        </div>
      </div>
      <div class="lh-1">
        <span
          v-if="visit.employee?.name"
          class="d-inline-flex flex-wrap align-center ga-2 text-high-emphasis text-wrap text-break"
          :class="clickableEmployee ? 'text-primary cursor-pointer' : ''"
          @click="clickableEmployee && emit('employeeClick', visit.employee)"
        >
          <EmployeeAvatarBlock
            :employee="employeeAvatar"
            :show-tooltip="false"
            horizontal
            truncate-three
            :max-width="mobile ? 'unset' : 225"
          />
          <VChip
            v-if="visit.employee?.deleted"
            size="x-small"
            class="my-1 m employee-avatar--deleted"
          >
            {{ $t('item_deleted') }}
          </VChip>
        </span>
      </div>
    </div>

    <VDivider :vertical="mdAndUp" />

    <!-- ======== WHAT ======== -->
    <div
      class="pa-3 flex-grow-1 d-flex flex-column justify-space-between"
      style="min-inline-size: 0;"
    >
      <div>
        <div
          v-if="visit.client"
          class="d-flex flex-wrap align-center lh-1"
        >
          <!--        -->
          <!--        <VIcon-->
          <!--          v-if="visit.employee?.name && visit.client?.name"-->
          <!--          icon="ri-circle-fill"-->
          <!--          size="4"-->
          <!--          class="text-disabled"-->
          <!--        />-->
          <span
            v-if="visit.client?.name"
            class="text-h6 font-weight-bold mr-2"
          >
            {{ visit.client.name }}
          </span>
          <span
            v-if="visit.client?.phone"
            class="text-caption text-disabled mr-2"
          >
            {{ visit.client.phone }}
          </span>
          <span
            v-if="visit.client?.email"
            class="text-caption text-disabled"
          >
            {{ visit.client.email }}
          </span>
        </div>
        <div
          class="d-flex flex-column ga-1"
          :class="{'mt-3': visit.client}"
        >
          <!-- Services -->
          <div
            v-for="(service, idx) in visit.services"
            :key="`s-${idx}`"
          >
            <VIcon
              icon="ri-stop-fill"
              size="8"
              class="text-disabled mr-2"
            />
            <span class="text-high-emphasis">{{ service.title }}</span>
            <span
              v-if="service.price != null"
              class="ml-2 text-medium-emphasis text-no-wrap"
            >
              <span :class="{ 'text-decoration-line-through': isDiscounted(service) }">
                {{ money(service.price) }}
              </span>
              <template v-if="isDiscounted(service)">
                <span class="text-green ml-2">{{ money(service.priceAfterDiscount) }}</span>
              </template>
            </span>
            <span
              v-if="showItemEmployee && service.employee"
              class="ml-2 text-caption text-disabled text-break text-wrap"
            >
              {{ service.employee }}
            </span>
          </div>

          <!-- Sold products -->
          <div
            v-for="(product, idx) in visit.products"
            :key="`p-${idx}`"
            style="margin-top: 2px;"
          >
            <VChip
              :size="mobile ? 'x-small' : 'x-small'"
              variant="flat"
              color="sale"
              class="mr-2 ml-n1"
              prepend-icon="ri-shopping-cart-line"
              label
            >
              {{ $t('sale') }}
            </VChip>
            <span class="text-high-emphasis">{{ product.name }}</span>
            <span
              v-if="product.package"
              class="ml-1 text-disabled"
            >
              ({{ packageLabel(product) }})
            </span>
            <span class="ml-2 text-medium-emphasis text-no-wrap">
              {{ product.quantity > 1 ? `${product.quantity}× ` : '' }}{{ money(product.value) }}
            </span>
            <span
              v-if="showItemEmployee && product.employee"
              class="ml-2 text-caption text-disabled text-break text-wrap"
            >
              {{ product.employee }}
            </span>
          </div>

          <!-- Cosmetics used up during the visit -->
          <div
            v-for="(product, idx) in visit.usedProducts"
            :key="`u-${idx}`"
          >
            <VChip
              :size="mobile ? 'x-small' : 'x-small'"
              color="consumption"
              variant="flat"
              class="mr-2 ml-n1"
              prepend-icon="ri-flask-line"
              label
            >
              <span style="margin-right: 9px;">{{ $t('consumption') }}</span>
            </VChip>
            <span class="text-high-emphasis">{{ product.name }}</span>
            <span
              v-if="product.package"
              class="ml-1 text-disabled"
            >
              ({{ packageLabel(product) }})
            </span>
            <span class="ml-2 text-medium-emphasis text-no-wrap">
              -{{ product.quantity }}&nbsp;{{ product.unit }}
            </span>
            <span
              v-if="product.value"
              class="ml-2 text-medium-emphasis text-no-wrap"
            >
              {{ money(product.value) }}
            </span>
            <span
              v-if="showItemEmployee && product.employee"
              class="ml-2 text-caption text-disabled text-break text-wrap"
            >
              {{ product.employee }}
            </span>
          </div>
        </div>
      </div>

      <!-- Cancellation reason -->
      <div
        v-if="visit.cancelReason || visit.cancelComment"
        class="d-flex mt-3 text-warning"
      >
        <VIcon
          icon="ri-calendar-close-line"
          color="error"
          size="16"
          class="mr-2 ml-n1"
        />
        <p class="mb-1 lh-1 text-body-2">
          <span v-if="visit.cancelReason">{{ visit.cancelReason }}</span>
          <span
            v-if="visit.cancelComment"
            class="text-medium-emphasis"
          >{{ visit.cancelReason ? ' — ' : '' }}{{ visit.cancelComment }}</span>
          <!--          <VTooltip activator="parent">-->
          <!--            {{ $t('cancellation_reason') }}-->
          <!--          </VTooltip>-->
        </p>
      </div>

      <!-- Event note -->
      <div
        v-if="visit.description?.length > 0"
        class="d-flex mt-3 text-medium-emphasis "
      >
        <VIcon
          icon="ri-double-quotes-r"
          size="16"
          class="mr-2 ml-n1"
        />
        <p class="visit-description mb-1 lh-1 text-body-2">
          {{ visit.description }}
        </p>
      </div>
    </div>


    <!-- ======== LOYALTY PROGRAMME ======== -->
    <VDivider
      v-if="showLoyaltyPoints"
      :vertical="mdAndUp"
    />
    <div
      v-if="showLoyaltyPoints && (mobile || !mdAndDown)"
      class="pa-3 flex-shrink-0 text-md-end"
      :style="[
        { minInlineSize: mobile ? undefined : width > 1680 ? '230px' : '145px' },
        { maxInlineSize: mobile ? undefined : width > 1680 ? '230px' : '145px' },
      ]"
    >
      <div class="text-caption text-uppercase text-disabled">
        {{ $t('loyaltyProgram') }}
      </div>
      <div class="text-caption text-medium-emphasis">
        {{ $t('collectedPoints') }}: <span class="font-weight-bold">{{ loyaltyPoints.gained }}</span>
      </div>
      <div class="text-caption text-medium-emphasis">
        {{ $t('usedPoints') }}: <span class="font-weight-bold">{{ loyaltyPoints.spent }}</span>
      </div>
    </div>

    <VDivider :vertical="mdAndUp" />
    <!-- ======== MONEY ======== -->
    <div
      class="pa-3 flex-shrink-0 text-md-end"
      :style="[
        { minInlineSize: mobile ? undefined : width > 1680 ? '280px' : '190px' },
        { maxInlineSize: mobile ? undefined : width > 1680 ? '280px' : '190px' },
      ]"
    >
      <div class="text-caption text-uppercase text-disabled">
        {{ $t('paid') }}
      </div>
      <div
        class="text-h6 font-weight-bold"
        :class="visit.paid ? 'text-high-emphasis' : 'text-disabled'"
      >
        {{ visit.paid ? money(visit.paid) : '—' }}
      </div>
      <div
        v-for="(payment, idx) in visit.payments"
        :key="idx"
        class="text-caption text-medium-emphasis"
      >
        {{ payment.name }}<small v-if="payment.isPrepayment"> ({{ $t('prepayment') }})</small>:
        {{ money(payment.value) }}
      </div>

      <div class="d-flex flex-wrap ga-1 mt-2 justify-md-end">
        <VChip
          v-for="(discount, idx) in visit.discounts"
          :key="idx"
          size="x-small"
          color="green"
          class="text-wrap h-auto py-1 lh-1 chip-safe-radius"
          variant="tonal"
        >
          {{ $t('discount') }} {{ discount.label }}: {{ money(discount.value) }}
        </VChip>
        <VChip
          v-if="visit.tip"
          size="x-small"
          variant="tonal"
        >
          {{ $t('tip') }}: {{ money(visit.tip) }}
        </VChip>
      </div>
      <div
        v-if="showLoyaltyPoints && (mdAndDown && !mobile)"
        class="mt-3"
      >
        <div class="text-caption text-uppercase text-disabled">
          {{ $t('loyaltyProgram') }}
        </div>
        <div class="text-caption text-medium-emphasis">
          {{ $t('collectedPoints') }}: <span class="font-weight-bold">{{ loyaltyPoints.gained }}</span>
        </div>
        <div class="text-caption text-medium-emphasis">
          {{ $t('usedPoints') }}: <span class="font-weight-bold">{{ loyaltyPoints.spent }}</span>
        </div>
      </div>

      <div
        v-if="$slots.actions"
        class="d-flex flex-wrap ga-2 mt-3 justify-md-end"
      >
        <slot
          name="actions"
          :visit="visit"
        />
      </div>
    </div>
  </VCard>
</template>

<style scoped>
.visit-description {
  white-space: pre-line;
}
</style>
